(ns pfo.task.model (:require
                     [clojure.string :refer [trim replace]]
                     [malli.core :as m]
                     [pfo.api :as api]
                     [pfo.auth.model :as auth-model]
                     [pfo.tz :as tz]
                     [pfo.time :refer [now]]
                     [cljs-time.core :as time-core :refer [plus years floor
                                                           minus day equal? days]]
                     [cljs-time.format :as time-format]
                     [cljs-time.coerce :as time-coerce]
                     [ajax.protocols :as protocol]
                     [ajax.formats :refer [raw-response-format]]
                     [ajax.json :refer [json-request-format]]
                     [pfo.util :as util :refer [to-value]]
                     [pfo.task.spec :as spec]
                     [pfo.link.model :as link-model]
                     [clojure.string :as string]))

(defn task-key [task] (str (:id task) "-" (get-in task [:date])))
(defn operation-key [operation] (str (:id operation) "-" (get-in operation [:complete_by])))
(defn task-assignment-key [task-assignment] (str "tc-" (:collection_id task-assignment)))
(defn rule-key [rule] (str "rule-" (:id rule)))

(def priorities {2 "Not specified"
                 3 "High"
                 1 "Low"})
(defn priority-name [task]
  (priorities (:priority task)))

(def invitation-statii {"n" nil
                        "p" " (pending)"
                        "a" " (accepted)"
                        "d" " (declined)"})

(def signature-type-none "n")
(def signature-type-step "s")
(def signature-type-cycle "c")
(def signature-type-document "d")
(def signature-type-boir "b")
(def signature-type-boir-link "l")
(def signature-type-boir-link-sign "ls")
(def signature-type-boir-e-file "e")
(def signature-type-dropbox-sign-delete "dd")
(def signature-type-dropbox-sign-retain "dr")

(def signature-types {signature-type-none "Regular"
                      signature-type-step "Step Signature"
                      signature-type-cycle "Review Signature"
                      signature-type-document "Document Signature"
                      signature-type-boir "BOIR Generate"
                      signature-type-boir-link "BOIR Link"
                      signature-type-boir-link-sign "BOIR Link & Step Signature"
                      signature-type-boir-e-file "E-File BOIR"
                      signature-type-dropbox-sign-delete "Dropbox Sign (delete unsigned)"
                      signature-type-dropbox-sign-retain "Dropbox Sign (retain unsigned)"})

(def nondependent-group-forbidden-step-types
  #{signature-type-step
    signature-type-cycle
    signature-type-boir
    signature-type-boir-link
    signature-type-boir-link-sign
    signature-type-boir-e-file})

(defn is-signature-step [step] (= signature-type-step (:signature_scope step)))
(defn is-cycle-signature-step [step] (= signature-type-cycle (:signature_scope step)))
(defn is-document-signature-step [step] (= signature-type-document (:signature_scope step)))
(defn is-boir-link-sign-step [step] (= signature-type-boir-link-sign (:signature_scope step)))
(defn is-any-signature-step [step] (or (is-signature-step step) (is-cycle-signature-step step) (is-document-signature-step step) (is-boir-link-sign-step step)))
(defn has-cycle-signature [step] (boolean (:cycle_signature_time step)))
(defn is-locked-by-signature [step]
  (or (and (or (is-signature-step step) (is-document-signature-step step) (is-boir-link-sign-step step)) (:complete step))
      (has-cycle-signature step)))
(defn is-dropbox-sign-step [step] (or (= signature-type-dropbox-sign-delete (:signature_scope step)) (= signature-type-dropbox-sign-retain (:signature_scope step))))
(defn is-dropbox-sign-signature-scope [signature-type]
  (some #(= % signature-type) [signature-type-dropbox-sign-delete signature-type-dropbox-sign-retain]))
(defn is-boir-efile-step [step] (= signature-type-boir-e-file (:signature_scope step)))

(def efile-status-initiated "submission_initiated")
(def efile-status-processing "submission_processing")
(def efile-status-validation-passed "submission_validation_passed")
(def efile-status-validation-failed "submission_validation_failed")
(def efile-status-accepted "submission_accepted")
(def efile-status-rejected "submission_rejected")
(def efile-status-failed "submission_failed")

(defn is-efile-pending [submission]
  (contains? #{efile-status-initiated
               efile-status-processing
               efile-status-validation-passed}
             (:status submission)))

(defn is-efile-accepted [submission]
  (= efile-status-accepted (:status submission)))

(defn is-efile-rejected [submission]
  (contains? #{efile-status-validation-failed
               efile-status-rejected
               efile-status-failed}
             (:status submission)))

(defn complete-by-date [task]
  (util/date-string (:complete_by task)))

(defn get-first-task-with-signature-type [tasks signature-type]
  (first (filter #(= (:signature_scope %) signature-type) tasks)))

(defn strip-step-prefix [task-name]
  (replace (trim (or task-name "")) #"^Step\s+\d+\s+-\s+" ""))

(defn templategroup-scope-name [group-id subscriber-id]
  (cond
    (not= nil group-id) "(Family)"
    (not= nil subscriber-id) "(Subscriber)"
    :else "(Global)"))

(def template-scope-family "family")
(def template-scope-subscriber "subscriber")
(def template-scope-global "global")

(defn templategroup-scope [group-id subscriber-id]
  (cond
    (not= nil group-id)      template-scope-family
    (not= nil subscriber-id) template-scope-subscriber
    :else                    template-scope-global))

(defn templategroup-name [tg]
  (str (:name tg) " " (templategroup-scope-name
                       (:collection_id tg)
                       (:subscriber_id tg))))

(defn task-name
  "Strips Step prefix, if present"
  [task]
  (strip-step-prefix (:name task)))

(defn step-name [index task-name]
  (str "Step " (+ 1 index) " - " (strip-step-prefix task-name)))

(defn group-task-name
  "Returns task's group name, if present. Otherwise behaves like task-name"
  [task]
  (or
   (when
    (and
     (or (:multi_step task) (get-in task [:group :multi_step]))
     (not-empty (:group_name task)))
     (:group_name task))
   (task-name task)))

(defn task-to-group-data [task]
  {:id (:group_id task)
   :name (:group_name task)
   :category_id (get-in task [:category :id])
   :category (:category task)
   :complete_by (:complete_by task)
   :repeat_until (:repeat_until task)
   :cycle (:cycle task)
   :rule (:rule task)
   :compliance (:compliance task)
   :editable (:editable task)
   :group_editable (:group_editable task)
   ;; line below: see comment for pfo.task.component/get-assignments
   :task_collections (:task_collections task)})

(defn should-end-time-be-adjusted [start-time end-time all-day?]
  (let [start-time-midnight (floor start-time day)
        end-time-midnight (floor end-time day)]
    (boolean (and all-day? (when (equal? end-time end-time-midnight)
                             (not (equal? start-time-midnight end-time-midnight)))))))

(defn event-type [start-time end-time all-day?]
  (let [start-time-midnight (floor start-time day)
        end-time-midnight (floor end-time day)
        end-time (if (should-end-time-be-adjusted start-time end-time all-day?) (minus end-time (days 1)) end-time)
        end-time-midnight (floor end-time day)]
    (if (equal? start-time-midnight end-time-midnight)
      (if all-day? :single-all-day :single-day)
      (if all-day?
        (if (= (time-core/year start-time) (time-core/year end-time)) :multi-all-day-same-year :multi-all-day)
        :multi-day))))

(defn event-time-string [event custom-date-format]
  (let [all-day (:all_day event)
        start-time (if all-day
                     (util/parse-datetime (:start event))
                     (util/parse-datetime-local (:client_start event)))
        end-time (if all-day
                   (util/parse-datetime (:end event))
                   (util/parse-datetime-local (:client_end event)))
        format-datetime (fn [aformat atime]
                          (time-format/unparse (time-format/formatter aformat) atime))
        format-time (fn [atime]
                      (let [aformat (if (zero? (time-core/minute atime)) "ha" "h:ma")]
                        (format-datetime aformat atime)))
        event-type (event-type start-time end-time all-day)
        end-time (if (and (contains? #{:single-all-day :multi-all-day :multi-all-day-same-year} event-type) (should-end-time-be-adjusted start-time end-time all-day))
                   (minus end-time (days 1))
                   end-time)

        date-format (or custom-date-format "EEE, MMMM d, yyyy")]

    (case event-type
      :single-all-day (format-datetime date-format start-time)
      :single-day (str (format-datetime date-format start-time) " " (format-time start-time) " - " (format-time end-time))
      :multi-all-day (str (format-datetime date-format start-time) " - " (format-datetime date-format end-time))
      :multi-all-day-same-year (str (format-datetime (or custom-date-format "EEE, MMMM d") start-time) " - " (format-datetime date-format end-time))
      :multi-day (str (format-datetime date-format start-time) " " (format-time start-time) " - " (format-datetime date-format end-time) " " (format-time end-time)))))

(defn event-time-string-breadcrumb [date custom-date-format]
  (let [format-datetime (fn [aformat atime]
                          (time-format/unparse (time-format/formatter aformat) atime))
        format-time (fn [atime]
                      (let [aformat (if (zero? (time-core/minute atime)) "ha" "h:ma")]
                        (format-datetime aformat atime)))
        date-format (or custom-date-format "EEE, MMMM d, yyyy")]

    (format-datetime date-format date)))

(defn task-permission [task task-perm-type]
  (let [pt-name (name task-perm-type)]
    (cond
      (= 2 (:type task)) (keyword (str "can_" pt-name "_event"))
      (:recurring task) (keyword (str "can_" pt-name "_compliance"))
      :else (keyword (str "can_" pt-name "_task")))))

(defn has-task-permission [task task-perm-type]
  (auth-model/has-family-perm (:group_collection_id task) (task-permission task task-perm-type)))

(defn operations-list [collection-id include-descendants from types user_ids]
  (api/ajaxp (str "/operations/?" (util/make-params (util/remove-nil-values {:collection_id collection-id
                                                                             :include_subcollections include-descendants
                                                                             :from from
                                                                             :type types
                                                                             :user_id user_ids
                                                                             :timezone (tz/get-user-timezone)}))) {}))

(defn filtered-task-query
  {:malli/schema [:=> [:cat
                       :string
                       spec/filter-params
                       :any] :any]}
  [url params-map link-parent]
  (api/ajaxp
   (str url "?"
        (util/make-params
         (util/remove-nil-values
          (merge
           (util/snakeize-keys params-map)
           {:timezone (tz/get-user-timezone)}
           (link-model/parent-params link-parent)))))
   {}))

(defn task-list [task-filter-params link-parent]
  (filtered-task-query "/tasks/" task-filter-params link-parent))

(defn template-shape-chart-counts [task-filter-params]
  (filtered-task-query "/task/charts/bar/" task-filter-params nil))

(defn task-report [collection-id page order include-descendants assigned-user-id task-type collection-type-id category-id year quarter
                   complete completed-from completed-to priority late today future]
  (api/ajaxp (str "/report/tasks/?" (util/make-params (util/remove-nil-values {:collection_id collection-id
                                                                               :page page
                                                                               :include_subcollections include-descendants
                                                                               :assigned_user_id assigned-user-id
                                                                               :order order
                                                                               :task_type task-type
                                                                               :collection_type_id collection-type-id
                                                                               :category_id category-id
                                                                               :year year
                                                                               :complete complete
                                                                               :completed_from completed-from
                                                                               :completed_to completed-to
                                                                               :late late
                                                                               :today today
                                                                               :future future
                                                                               :priority priority
                                                                               :timezone (tz/get-user-timezone)
                                                                               :quarter quarter}))) {:response-format {:content-type "application/pdf"
                                                                                                                       :description "pdf"
                                                                                                                       :read protocol/-body
                                                                                                                       :type :arraybuffer}}))

(defn task-add [form-data]
  (api/ajaxp "/tasks/"
             {:method :post
              :body (util/make-params (util/remove-nil-values form-data))}))

(defn task-add-to-group [group-id form-data]
  (api/ajaxp (str "/taskgroup/" group-id "/tasks/")
             {:method :post
              :body (util/make-params (util/remove-nil-values form-data))}))

(defn group-update [group-id form-data]
  (api/ajaxp (str "/taskgroup/" group-id "/")
             {:method :post
              :body (util/make-params (util/remove-nil-values form-data))}))

(defn group-delete [group-id control-phrase]
  (api/ajaxp
   (str "/taskgroup/" group-id "/"
        (when (and control-phrase (not-empty @control-phrase))
          (str "?"
               (util/make-params (util/remove-nil-values {:control_phrase @control-phrase})))))
   {:method :delete}))

(defn single-task-update [task-id form-data]
  (api/ajaxp (str "/single/tasks/" task-id "/")
             {:method :post
              :body (util/make-params (util/remove-nil-values form-data))}))

(defn single-task-to-multistep [task-id]
  (api/ajaxp (str "/convert/tasks/" task-id "/multi/")
             {:method :post}))

(defn multistep-task-to-singlestep [task-id]
  (api/ajaxp (str "/convert/tasks/" task-id "/single/")
             {:method :post}))

(defn task-update [task-id form-data]
  (api/ajaxp (str "/tasks/" task-id "/")
             {:method :post
              :body (util/make-params (util/remove-nil-values form-data))}))

(defn task-delete [task-id complete-by force control-phrase]
  (api/ajaxp
   (str "/compliance/" task-id "/" complete-by "/?" (util/make-params (util/remove-nil-values {:force (when force "yes")
                                                                                               :control_phrase (when (and control-phrase (not-empty @control-phrase))
                                                                                                                 @control-phrase)})))
   {:method :delete}))

(defn task-toggle-complete [options task-id complete-by complete token]
  (api/ajaxp (str "/tasks/" task-id "/" complete-by "/toggle-complete/")
             {:method :post
              :params (util/remove-nil-values {:complete complete
                                               :auth_token token})
              :skip-default-fail (:skip-default-fail options)}))

(defn task-details [id complete_by]
  (api/ajaxp (str "/tasks/" id "/" complete_by "/") {:params {:timezone (tz/get-user-timezone)}}))

(defn task-details-without-date [id]
  (api/ajaxp (str "/tasks/" id "/") {:params {:timezone (tz/get-user-timezone)}}))

(defn task-group-details [group-id]
  (api/ajaxp (str "/taskgroup/" group-id "/") {:params {:timezone (tz/get-user-timezone)}}))

(defn occurrence-details [id]
  (api/ajaxp (str "/occurrences/" id "/") {:params {:timezone (tz/get-user-timezone)}}))

(defn task-editable-cycle [id complete-by]
  (api/ajaxp (str "/compliance/" id "/" complete-by "/") {}))

(defn rule-list []
  (api/ajaxp (str "/rules/") {}))

(defn templategroup-list [group-id atype order]
  (api/ajaxp (str "/templategroup/") {:params (util/remove-nil-values {:group_id group-id
                                                                       :type atype
                                                                       :order order
                                                                       :filter_active "active"})}))

(defn templategroup-apply [form-data]
  (api/ajaxp "/templategroups/apply/"
             {:method :post
              :params (util/remove-nil-values (select-keys form-data [:group_id :collection_id :template_group_id :user_id :complete_by
                                                                      :parent_type :parent_key :link :parent_id :parent_complete_by]))}))

(defn templategroup-apply-bulk [form-data]
  (api/ajaxp "/templategroups/apply/bulk/"
             {:method :post
              :params (util/remove-nil-values (select-keys form-data [:group_id :collection_id :template_group_id :user_id :complete_by]))
              :disable-loadin-counter true}))

(defn event-list [collection-id include-descendants start-date end-date collection-type-id collection-type-exclude category-id invited-user-ids per-page page calendar first_only]
  (api/ajaxp (str "/events/?" (util/make-params (util/remove-nil-values {:collection_id collection-id
                                                                         :include_subcollections include-descendants
                                                                         :start start-date
                                                                         :end end-date
                                                                         :collection_type_id collection-type-id
                                                                         :collection_type_exclude collection-type-exclude
                                                                         :category_id category-id
                                                                         :attendee_user_id invited-user-ids
                                                                         :page page
                                                                         :per_page per-page
                                                                         :calendar (when calendar 1)
                                                                         :timezone (tz/get-user-timezone)
                                                                         :first_only first_only}))) {}))

(defn event-occurrences [id start-date end-date]
  (api/ajaxp (str "/events/?" (util/make-params (util/remove-nil-values {:start start-date
                                                                         :end end-date
                                                                         :calendar 1
                                                                         :id id
                                                                         :timezone (tz/get-user-timezone)}))) {}))

(defn my-event-list []
  (let [start-date (now)
        end-date (plus start-date (years 1))]
    (api/ajaxp "/events/" {:params (util/remove-nil-values {:start (util/date-to-rfc3339 start-date) ;;(util/local-date-time-to-date-string start-date)
                                                            :end (util/date-to-rfc3339 end-date)
                                                            :attendee_user_id (api/get-auth-info :user-id)
                                                            :first_only true
                                                            :timezone (tz/get-user-timezone)})})))

(defn event-add [form-data]
  (api/ajaxp "/events/"
             {:method :post
              :body (util/make-params (util/remove-nil-values form-data))}))

(defn event-update [event-id form-data]
  (api/ajaxp (str "/events/" event-id "/")
             {:method :post
              :body (util/make-params (util/remove-nil-values form-data))}))


(defn event-details [akey]
  (api/ajaxp (str "/event/" akey "/") {:params {:timezone (tz/get-user-timezone)}}))

(defn event-details-without-date [id]
  (api/ajaxp (str "/events/" id "/") {:params {:timezone (tz/get-user-timezone)}}))

(defn event-details-silent [akey]
  (api/ajaxp (str "/event/" akey "/") {:disable-loadin-counter true
                                       :params {:timezone (tz/get-user-timezone)}}))

(defn event-comments-summary [akey]
  (api/ajaxp (str "/event/" akey "/comments/summary/") {}))


(defn task-comments [task-id complete-by task-type]
  (api/ajaxp (str
              "/task/" task-id "/comment/?"
              (util/make-params {:complete_by complete-by
                                 :type task-type})) {}))

(defn task-add-comment [task-id complete-by conversation-id note subject body notify-user-ids notify-cc-user-ids include-body allow-inbox-reply task_type revised-comment-id]
  (api/ajaxp
    (str "/task/" task-id "/comment/")
    {:method :post
     :body (util/make-params
             (util/remove-nil-values {:conversation_id conversation-id
                                      :body body
                                      :complete_by complete-by
                                      :note note
                                      :subject subject
                                      :notify_user_id notify-user-ids
                                      :notify_cc_user_id notify-cc-user-ids
                                      :type task_type
                                      :include_body include-body
                                      :allow_inbox_reply allow-inbox-reply
                                      :revised_comment_id revised-comment-id}))}))

(defn task-update-comment [task-id comment-id subject body notify-user-ids notify-cc-user-ids include-body allow-inbox-reply]
  (api/ajaxp
    (str "/task/" task-id "/comment/" comment-id)
    {:method :post
     :body (util/make-params
             (util/remove-nil-values {:subject subject
                                      :body body
                                      :notify_user_id notify-user-ids
                                      :notify_cc_user_id notify-cc-user-ids
                                      :include_body include-body
                                      :allow_inbox_reply allow-inbox-reply}))}))

(defn task-delete-comment [task-id comment-id notify-user-ids]
  (api/ajaxp
   (str
    "/task/" task-id "/comment/" comment-id "/?"
    (util/make-params
     (util/remove-nil-values {:notify_user_id notify-user-ids})))
   {:method :delete}))

(defn task-comments-summary [task-id complete-by]
  (api/ajaxp
   (str "/task/" task-id "/comment/")
   {:params (util/remove-nil-values {:complete_by complete-by
                                     :summary "yes"})}))

(defn task-filter-dates [task-type collection-id include-subcollections complete]
  (api/ajaxp "/filter/tasks/dates/" {:params (util/remove-nil-values {:task_type task-type
                                                                      :collection_id collection-id
                                                                      :include_subcollections include-subcollections
                                                                      :timezone (tz/get-user-timezone)
                                                                      :complete complete})}))

(defn task-filter-categories [task-type collection-id include-subcollections]
  (api/ajaxp "/filter/tasks/categories/" {:params (util/remove-nil-values {:task_type task-type
                                                                           :collection_id collection-id
                                                                           :timezone (tz/get-user-timezone)
                                                                           :include_subcollections include-subcollections})}))

(defn task-filter-collection-types [task-type collection-id include-subcollections]
  (api/ajaxp "/filter/tasks/collection_types/" {:params (util/remove-nil-values {:task_type task-type
                                                                                 :collection_id collection-id
                                                                                 :timezone (tz/get-user-timezone)
                                                                                 :include_subcollections include-subcollections})}))

(defn task-filter-assigned-users [task-type collection-id include-subcollections complete]
  (api/ajaxp "/filter/tasks/users/" {:params (util/remove-nil-values
                                              {:task_type task-type
                                               :collection_id collection-id
                                               :include_subcollections include-subcollections
                                               :timezone (tz/get-user-timezone)
                                               :complete complete})}))

(defn task-filter-assignable-users [task-type collection-id include-subcollections]
  (api/ajaxp "/filter/tasks/users/assignable/"
             {:params (util/remove-nil-values
                       {:task_type task-type
                        :collection_id collection-id
                        :timezone (tz/get-user-timezone)
                        :include_subcollections include-subcollections})}))

(defn event-filter-invited-users [task-type collection-id include-subcollections]
  (api/ajaxp "/filter/events/users/" {:params (util/remove-nil-values
                                               {:task_type task-type
                                                :collection_id collection-id
                                                :timezone (tz/get-user-timezone)
                                                :include_subcollections include-subcollections})}))

(defn bulk-edit [group-id filter-data form-data]
  (api/ajaxp "/bulk/tasks/" {:params {:filter (into {:timezone (tz/get-user-timezone)} (util/remove-nil-values filter-data))
                                      :form (assoc (into {} (util/remove-nil-values form-data))
                                                   :group group-id)}
                             :format (json-request-format)
                             :method :post}))

(defn task-list-filter-params [{:keys [collection-id
                                       page
                                       order
                                       include-descendants
                                       assigned-user-id
                                       task-type
                                       collection-type-id
                                       category-id
                                       rule-id
                                       year
                                       quarter
                                       complete
                                       completed-from
                                       completed-to
                                       priority
                                       late
                                       today
                                       future
                                       signature
                                       steps
                                       step-number
                                       link-parent
                                       dropbox-sign
                                       dropbox-sign-pending
                                       e-boir-not-started
                                       e-boir-pending
                                       e-boir-rejected
                                       e-boir
                                       e-boir-failed
                                       labels
                                       alert-enabled
                                       alert-active]}]
  {:collection_id          collection-id
   :page                   page
   :include_subcollections include-descendants
   :assigned_user_id       assigned-user-id
   :order                  order
   :task_type              task-type
   :collection_type_id     collection-type-id
   :category_id            category-id
   :rule_id                rule-id
   :year                   year
   :complete               complete
   :completed_from         completed-from
   :completed_to           completed-to
   :late                   late
   :today                  today
   :future                 future
   :signature              signature
   :priority               priority
   :quarter                quarter
   :steps                  steps
   :timezone               (tz/get-user-timezone)
   :parent_type            (:parent-type link-parent)
   :parent_id              (:parent-id link-parent)
   :step_number            (when steps step-number)
   :dropbox_sign           dropbox-sign
   :dropbox_sign_pending   dropbox-sign-pending
   :e_boir_not_started     e-boir-not-started
   :e_boir_pending         e-boir-pending
   :e_boir_rejected        e-boir-rejected
   :e_boir                 e-boir
   :e_boir_failed          e-boir-failed
   :label_ids              labels
   :alert_enabled          alert-enabled
   :alert_active           alert-active})

(defn add-task-link [filter-data
                     parent-type
                     parent-id
                     parent-complete-by
                     child-type
                     child-id
                     select]
  (api/ajaxp "/links/task/"
             {:method :post
              :body   (util/make-params
                       (util/remove-nil-values
                        (merge
                         (task-list-filter-params filter-data)
                         {:parent_type        parent-type
                          :parent_id          parent-id
                          :parent_complete_by parent-complete-by
                          :child_type         child-type
                          :child_id           child-id
                          :select             select})))}))

(defn toggle-alert [task-id enabled before-due-days past-due-days]
  (api/ajaxp (str "/task/" task-id "/alert")
             {:method :post
              :body (util/make-params
                     (util/remove-nil-values {:enabled enabled
                                              :before_due_days before-due-days
                                              :past_due_days past-due-days}))}))

(defn alert-count []
  (api/ajaxp (str "/task/alerts/count/") {:disable-loadin-counter true}))

(defn alert-list [page order]
  (api/ajaxp (str "/task/alerts/") {:params (util/remove-nil-values {:page page
                                                                     :order order})}))

